import { Modal, MODAL_TALL_HEIGHT, ModalSize } from 'aos-ui-universal'
import React, { FCWithChildren, PropsWithChildren, ReactNode, useRef } from 'react'
import styled from 'styled-components'

import { DropdownRoot } from '../appcontainer/AppContainer'
import { Box } from '../base/Box'
import { DropdownRootProvider } from '../form/dropdown/base/DropdownRoot'
import { Spinner } from '../ui/Spinner'

interface MapAwareModalProps {
    id?: string
    size?: ModalSize
    isOpen: boolean
    content?: ReactNode
    contentHeader?: ReactNode
    contentFooter?: ReactNode
    map?: ReactNode
    mapHeader?: ReactNode
    mapFooter?: ReactNode
    view: DialogView
    isLoading?: boolean
    closeAction(): void
}

export enum DialogView {
    Content,
    Map,
}

export const MapAwareModal: FCWithChildren<PropsWithChildren<MapAwareModalProps>> = ({
    size = 'xl',
    id,
    isOpen,
    isLoading,
    closeAction,
    view,
    map,
    mapFooter,
    mapHeader,
    content,
    contentFooter,
    contentHeader,
}) => {
    const [header, footer] =
        view === DialogView.Content ? [contentHeader, contentFooter] : [mapHeader, mapFooter]

    const rootRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    if (isLoading) {
        return (
            <Modal
                nativeID={id}
                isOpen={isOpen}
                onClose={closeAction}
                title={header}
                size={size}
                className={MODAL_TALL_HEIGHT}
                bodyClassName='items-center justify-center'
            >
                <Spinner />
            </Modal>
        )
    }

    return (
        <Modal
            nativeID={id}
            isOpen={isOpen}
            onClose={closeAction}
            title={header}
            footer={footer}
            size={size}
            className={MODAL_TALL_HEIGHT}
            scrollable={false}
        >
            <DropdownRootProvider value={{ container: containerRef, dropdownRoot: rootRef }}>
                <Box relative flex={1} ref={containerRef}>
                    <Wrapper view={view}>
                        <Content show={view === DialogView.Content}>{content}</Content>
                        <Map>{view === DialogView.Map && map}</Map>
                    </Wrapper>
                    <DropdownRoot ref={rootRef} />
                </Box>
            </DropdownRootProvider>
        </Modal>
    )
}

const Wrapper = styled(Box).attrs(() => ({ flex: 1 }))<{ view: DialogView }>`
    position: relative;
    transform: translateX(${p => (p.view === DialogView.Map ? '-100%' : '0')});
    transition: transform 0.3s ease-in-out;
    flex: 1;
    height: 100%;
`

const Content = styled(Box)<{ show: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: ${({ show }) => (show ? 'block' : 'none')};
    flex: 1;
`

const Map = styled(Box)`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    transform: translateX(100%);
    flex: 1;
`
